import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Pencil,
  ChevronDown,
  ChevronUp,
  Plus,
  FileText,
  LinkIcon,
  Paperclip,
  Trash2,
  X,
  ExternalLink,
  Download,
} from 'lucide-react';
import { cn, Button, LessonMarkdown } from '@jeraisonne/ui';
import type { ContentStatus, LessonResource } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useFeatureFlag } from '@/lib/feature-flags';
import { useAdminLessonDetail } from '@/lib/admin/queries';
import {
  useLessonResources,
  useLessonExercises,
  useCreateTextResource,
  useCreateLinkResource,
  useCreateFileResource,
  useDeleteLessonResource,
  getResourcePublicUrl,
  formatFileSize,
} from '@/lib/admin/lesson-resources';
import { MarkdownMath } from '@jeraisonne/ui';

const STATUS_STYLES: Record<ContentStatus, string> = {
  published: 'bg-xp-100 text-xp-700',
  draft: 'bg-ink-100 text-ink-600',
  archived: 'bg-accent-50 text-accent-600',
  awaiting_review: 'bg-amber-50 text-amber-700',
};

const STATUS_LABEL: Record<ContentStatus, string> = {
  published: 'Publié',
  draft: 'Brouillon',
  archived: 'Archivé',
  awaiting_review: 'En relecture',
};

const DIFFICULTY_LABEL: Record<'easy' | 'medium' | 'hard', string> = {
  easy: 'Facile',
  medium: 'Moyen',
  hard: 'Difficile',
};

// ── Resource sub-forms ─────────────────────────────────────────────────────────

function AddTextResourceForm({ lessonId, onDone }: { lessonId: string; onDone: () => void }) {
  const createText = useCreateTextResource();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    await createText.mutateAsync({
      lesson_id: lessonId,
      title: title.trim(),
      content_md: content,
    });
    onDone();
  }

  return (
    <form onSubmit={handleSubmit} className="bg-primary-50 space-y-2 rounded-xl p-3">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Titre de la ressource texte"
        className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
        required
        autoFocus
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={6}
        placeholder="Contenu en Markdown…"
        className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full resize-y rounded-lg border px-2.5 py-2 font-mono text-[13px] focus:outline-none focus:ring-2"
        required
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" size="sm" disabled={createText.isPending || !title.trim()}>
          {createText.isPending ? '...' : 'Ajouter'}
        </Button>
      </div>
      {createText.isError && (
        <p className="text-accent-600 font-sans text-[12px]">{createText.error?.message}</p>
      )}
    </form>
  );
}

function AddLinkResourceForm({ lessonId, onDone }: { lessonId: string; onDone: () => void }) {
  const createLink = useCreateLinkResource();
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !url.trim()) return;
    await createLink.mutateAsync({
      lesson_id: lessonId,
      title: title.trim(),
      url: url.trim(),
    });
    onDone();
  }

  return (
    <form onSubmit={handleSubmit} className="bg-primary-50 space-y-2 rounded-xl p-3">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Titre du lien"
        className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
        required
        autoFocus
      />
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://..."
        className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
        required
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" size="sm" disabled={createLink.isPending || !title.trim()}>
          {createLink.isPending ? '...' : 'Ajouter'}
        </Button>
      </div>
      {createLink.isError && (
        <p className="text-accent-600 font-sans text-[12px]">{createLink.error?.message}</p>
      )}
    </form>
  );
}

function AddFileResourceForm({ lessonId, onDone }: { lessonId: string; onDone: () => void }) {
  const createFile = useCreateFileResource();
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f && !title.trim()) {
      setTitle(f.name.replace(/\.[^.]+$/, ''));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !file) return;
    await createFile.mutateAsync({
      lesson_id: lessonId,
      title: title.trim(),
      file,
    });
    onDone();
  }

  return (
    <form onSubmit={handleSubmit} className="bg-primary-50 space-y-2 rounded-xl p-3">
      <input
        type="file"
        onChange={handleFileChange}
        className="text-ink-700 file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200 w-full font-sans text-[12px] file:mr-3 file:rounded-lg file:border-0 file:px-3 file:py-1.5 file:text-[12px] file:font-semibold"
        required
      />
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Titre du fichier"
        className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
        required
      />
      {file && (
        <p className="text-ink-500 font-mono text-[11px]">
          {file.name} · {formatFileSize(file.size)}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" size="sm" disabled={createFile.isPending || !file || !title.trim()}>
          {createFile.isPending ? 'Upload…' : 'Ajouter'}
        </Button>
      </div>
      {createFile.isError && (
        <p className="text-accent-600 font-sans text-[12px]">{createFile.error?.message}</p>
      )}
    </form>
  );
}

// ── Resource row ───────────────────────────────────────────────────────────────

function ResourceRow({ resource }: { resource: LessonResource }) {
  const [expanded, setExpanded] = useState(false);
  const deleteResource = useDeleteLessonResource();

  function handleDelete() {
    if (!confirm(`Supprimer la ressource "${resource.title}" ?`)) return;
    deleteResource.mutate({
      id: resource.id,
      lesson_id: resource.lesson_id,
      file_path: resource.file_path,
    });
  }

  const isPdf = resource.mime_type === 'application/pdf';
  const fileUrl = resource.file_path ? getResourcePublicUrl(resource.file_path) : null;

  return (
    <div className="border-ink-100 border-b last:border-b-0">
      <div className="flex items-center gap-2 p-3">
        <span className="text-primary-500 shrink-0">
          {resource.kind === 'text' && <FileText className="h-4 w-4" />}
          {resource.kind === 'link' && <LinkIcon className="h-4 w-4" />}
          {resource.kind === 'file' && <Paperclip className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 truncate text-[13px] font-semibold">
            {resource.title}
          </p>
          {resource.kind === 'file' && resource.file_name && (
            <p className="text-ink-400 truncate font-mono text-[10px]">
              {resource.file_name} · {formatFileSize(resource.file_size)}
            </p>
          )}
          {resource.kind === 'link' && resource.url && (
            <p className="text-ink-400 truncate font-mono text-[10px]">{resource.url}</p>
          )}
        </div>

        {resource.kind === 'text' && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-ink-400 hover:text-ink-700 hover:bg-ink-100 rounded-lg p-1.5 transition-colors"
            aria-label={expanded ? 'Replier' : 'Déplier'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}

        {resource.kind === 'link' && resource.url && (
          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded-lg p-1.5 transition-colors"
            aria-label="Ouvrir le lien"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}

        {resource.kind === 'file' && fileUrl && (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={!isPdf ? (resource.file_name ?? undefined) : undefined}
            className="text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded-lg p-1.5 transition-colors"
            aria-label={isPdf ? 'Ouvrir le PDF' : 'Télécharger'}
          >
            {isPdf ? <ExternalLink className="h-4 w-4" /> : <Download className="h-4 w-4" />}
          </a>
        )}

        <button
          onClick={handleDelete}
          disabled={deleteResource.isPending}
          className="text-ink-400 hover:text-accent-600 hover:bg-accent-50 rounded-lg p-1.5 transition-colors disabled:opacity-50"
          aria-label="Supprimer"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {resource.kind === 'text' && expanded && resource.content_md && (
        <div className="bg-ink-50 border-ink-100 border-t px-4 py-3">
          <MarkdownMath className="text-ink-800 text-[13px]">{resource.content_md}</MarkdownMath>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

type AddKind = 'text' | 'link' | 'file' | null;

export function TutorAdminLessonDetailPage() {
  const { lessonId } = useParams({ strict: false }) as { lessonId: string };
  const { data: lesson, isLoading } = useAdminLessonDetail(lessonId);
  const { data: exercises = [] } = useLessonExercises(lessonId);
  const { data: resources = [] } = useLessonResources(lessonId);
  const { profile } = useAuth();
  const v2EditorEnabled = useFeatureFlag('lesson_v2_editor', profile);

  const [exercisesOpen, setExercisesOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const [addKind, setAddKind] = useState<AddKind>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="p-6 text-center">
        <p className="font-display text-ink-600 font-semibold">Leçon introuvable.</p>
        <Link
          to="/tutor/admin/chapters"
          className="text-primary-600 mt-2 inline-block text-[13px] underline"
        >
          Retour aux chapitres
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      {/* Back */}
      <Link
        to="/tutor/admin/chapters/$chapterId"
        params={{ chapterId: lesson.chapter_id }}
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {lesson.chapter?.title ?? 'Chapitre'}
      </Link>

      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-ink-900 text-[22px] font-extrabold leading-tight">
            {lesson.title}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="text-ink-400 font-mono text-[11px]">{lesson.slug}</span>
            <span
              className={cn(
                'font-display rounded-full px-2 py-0.5 text-[11px] font-semibold',
                STATUS_STYLES[lesson.status],
              )}
            >
              {STATUS_LABEL[lesson.status]}
            </span>
          </div>
        </div>
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
          {v2EditorEnabled ? (
            <Link to="/tutor/admin/lessons/$lessonId/edit-v2" params={{ lessonId }}>
              <Button
                size="sm"
                className="flex min-h-11 w-full items-center justify-center gap-1.5 sm:w-auto"
              >
                <Pencil className="h-3.5 w-3.5" />
                Éditer en V2
              </Button>
            </Link>
          ) : null}
          <Link to="/tutor/admin/lessons/$lessonId/edit" params={{ lessonId }}>
            <Button
              size="sm"
              variant="outline"
              className="flex min-h-11 w-full items-center justify-center gap-1.5 sm:w-auto"
            >
              <Pencil className="h-3.5 w-3.5" />
              Modifier la leçon
            </Button>
          </Link>
        </div>
      </div>

      {/* Lesson content collapsible */}
      <section className="border-ink-200 mb-5 overflow-hidden rounded-2xl border bg-white">
        <button
          onClick={() => setContentOpen((v) => !v)}
          className="hover:bg-ink-50 flex w-full items-center gap-2 px-4 py-3 text-left transition-colors"
        >
          <FileText className="text-primary-500 h-4 w-4 shrink-0" />
          <span className="font-display text-ink-900 flex-1 text-[14px] font-semibold">
            Contenu de la leçon
          </span>
          {contentOpen ? (
            <ChevronUp className="text-ink-400 h-4 w-4" />
          ) : (
            <ChevronDown className="text-ink-400 h-4 w-4" />
          )}
        </button>
        {contentOpen && (
          <div className="border-ink-100 border-t px-4 py-3">
            {lesson.content_md ? (
              <LessonMarkdown className="text-ink-800 text-[13px]">
                {lesson.content_md}
              </LessonMarkdown>
            ) : (
              <p className="text-ink-400 font-sans text-[13px] italic">Pas encore de contenu.</p>
            )}
          </div>
        )}
      </section>

      {/* Exercises section (collapsed by default) */}
      <section className="border-ink-200 mb-5 overflow-hidden rounded-2xl border bg-white">
        <button
          onClick={() => setExercisesOpen((v) => !v)}
          className="hover:bg-ink-50 flex w-full items-center gap-2 px-4 py-3 text-left transition-colors"
        >
          <span className="font-display text-ink-900 flex-1 text-[14px] font-semibold">
            Exercices rattachés ({exercises.length})
          </span>
          {exercisesOpen ? (
            <ChevronUp className="text-ink-400 h-4 w-4" />
          ) : (
            <ChevronDown className="text-ink-400 h-4 w-4" />
          )}
        </button>
        {exercisesOpen && (
          <div className="border-ink-100 border-t">
            {exercises.length > 0 ? (
              <div className="divide-ink-100 divide-y">
                {exercises.map((ex) => (
                  <Link
                    key={ex.id}
                    to="/tutor/exercises/$exerciseId"
                    params={{ exerciseId: ex.id }}
                    className="hover:bg-ink-50 flex items-center gap-2 px-4 py-3 transition-colors active:scale-[0.99]"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-ink-900 truncate text-[13px] font-semibold">
                        {ex.title}
                      </p>
                      <p className="text-ink-400 font-mono text-[11px]">
                        {DIFFICULTY_LABEL[ex.difficulty]}
                        {ex.estimated_minutes ? ` · ${ex.estimated_minutes} min` : ''}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'font-display shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                        STATUS_STYLES[ex.status],
                      )}
                    >
                      {STATUS_LABEL[ex.status]}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-ink-400 px-4 py-3 font-sans text-[13px] italic">
                Aucun exercice rattaché à cette leçon.
              </p>
            )}
            <div className="border-ink-100 border-t p-3">
              <Link to="/tutor/exercises/new" search={{ chapterId: lesson.chapter_id, lessonId }}>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex min-h-[2.75rem] w-full items-center justify-center gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter un exercice
                </Button>
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Resources section */}
      <section className="border-ink-200 mb-5 overflow-hidden rounded-2xl border bg-white">
        <div className="border-ink-100 flex items-center gap-2 border-b px-4 py-3">
          <Paperclip className="text-primary-500 h-4 w-4 shrink-0" />
          <span className="font-display text-ink-900 flex-1 text-[14px] font-semibold">
            Ressources ({resources.length})
          </span>
        </div>

        {resources.length > 0 ? (
          <div>
            {resources.map((res) => (
              <ResourceRow key={res.id} resource={res} />
            ))}
          </div>
        ) : (
          <p className="text-ink-400 px-4 py-3 font-sans text-[13px] italic">
            Aucune ressource pour cette leçon.
          </p>
        )}

        <div className="border-ink-100 border-t p-3">
          {addKind === null ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                size="sm"
                variant="outline"
                className="flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 sm:w-auto"
                onClick={() => setAddKind('text')}
              >
                <FileText className="h-4 w-4" />
                Texte
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 sm:w-auto"
                onClick={() => setAddKind('file')}
              >
                <Paperclip className="h-4 w-4" />
                Fichier
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 sm:w-auto"
                onClick={() => setAddKind('link')}
              >
                <LinkIcon className="h-4 w-4" />
                Lien
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-display text-ink-700 text-[12px] font-semibold">
                  Nouvelle ressource —{' '}
                  {addKind === 'text' ? 'texte' : addKind === 'file' ? 'fichier' : 'lien'}
                </span>
                <button
                  onClick={() => setAddKind(null)}
                  className="text-ink-400 hover:text-ink-700 rounded-lg p-1 transition-colors"
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {addKind === 'text' && (
                <AddTextResourceForm lessonId={lessonId} onDone={() => setAddKind(null)} />
              )}
              {addKind === 'file' && (
                <AddFileResourceForm lessonId={lessonId} onDone={() => setAddKind(null)} />
              )}
              {addKind === 'link' && (
                <AddLinkResourceForm lessonId={lessonId} onDone={() => setAddKind(null)} />
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
